import { Link } from "react-router-dom";
import { ArrowRight, Check, Ruler } from "lucide-react";
import { MediaImage } from "../MediaImage";
import { useSiteMedia } from "../../lib/siteMedia";
import { trackEvent } from "../../lib/api";
import { ISDE_PATH } from "../../lib/isde";
import { COMPARE_PATH } from "../../data/compareContent";
import { SERVICE_SCOPE, TRUST_POINTS } from "../../data/homeContent";
import { PhoneLink, QuoteButton } from "./CtaButtons";

const ScopeList = () => (
  <ul className="mt-5 flex flex-wrap gap-1.5" aria-label="Wat wij voor u verzorgen" data-testid="hero-scope">
    {SERVICE_SCOPE.map((s) => (
      <li key={s} className="inline-flex items-center gap-1.5 rounded-full border border-maas/20 bg-maas-soft px-2.5 py-1.5 text-[13px] font-semibold text-maas-deep">
        <Check className="h-3.5 w-3.5 text-maas" aria-hidden="true" /> {s}
      </li>
    ))}
  </ul>
);

// Tekst na de eerste komma krijgt de accentmarkering.
const Title = ({ text }) => {
  const i = text.indexOf(", ");
  if (i < 0) return text;
  return <>{text.slice(0, i + 1)} <span className="hero-mark">{text.slice(i + 2)}</span></>;
};

// Route 2 en 3 als rustige tekstlinks; de prijsindicatie blijft de enige grote knop.
const SideRoutes = () => (
  <ul className="mt-5 grid gap-2 text-[14.5px] sm:flex sm:flex-wrap sm:gap-x-6" data-testid="hero-routes">
    <li><Link to={COMPARE_PATH} state={{ from: "/" }} onClick={() => trackEvent("cta_compare_click", { location: "hero" })} className="group inline-flex items-center gap-1.5 font-semibold text-ink hover:text-maas" data-testid="hero-route-compare">
      Al een offerte? Laat hem vergelijken <ArrowRight className="h-4 w-4 text-maas transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></Link></li>
    <li><Link to={`${ISDE_PATH}#subsidiecheck`} onClick={() => trackEvent("cta_isde_click", { location: "hero" })} className="group inline-flex items-center gap-1.5 font-semibold text-ink hover:text-maas" data-testid="hero-route-isde">
      Check uw mogelijke ISDE-subsidie <ArrowRight className="h-4 w-4 text-maas transition-transform group-hover:translate-x-0.5" aria-hidden="true" /></Link></li>
  </ul>
);

export const HomeHero = ({ block }) => {
  const media = useSiteMedia();
  return (
    <section aria-labelledby="hero-title" className="relative overflow-hidden bg-white" data-testid="home-hero">
      <div className="hero-grid absolute inset-y-0 right-0 hidden w-[55%] lg:block" aria-hidden="true" />
      <div className="container-tight relative grid items-center gap-10 pb-12 pt-6 sm:pt-10 lg:grid-cols-12 lg:gap-12 lg:pb-16 lg:pt-12">
        <div className="reveal-hero lg:col-span-6">
          <p className="eyebrow">Kozijnspecialist uit Maassluis</p>
          <h1 id="hero-title" className="h1 mt-3 text-balance lg:text-[2.6rem] xl:text-[3.125rem]" data-testid="hero-title"><Title text={block.title} /></h1>
          <p className="lead mt-4 max-w-xl" data-testid="hero-lead">{block.body}</p>
          <ScopeList />
          <div className="mt-7 flex flex-col items-start gap-3">
            <QuoteButton location="hero" className="w-full sm:w-auto sm:whitespace-nowrap" />
            <PhoneLink location="hero" />
          </div>
          <SideRoutes />
        </div>
        <div className="reveal-hero relative lg:col-span-6 [animation-delay:120ms]">
          <div className="frame">
            <MediaImage media={media("home_hero")} priority sizes="(min-width: 1024px) 620px, calc(100vw - 32px)"
              className="aspect-[4/3] rounded-[14px] sm:aspect-[3/2]" imgClassName="object-[50%_45%]" testId="hero-image" />
          </div>
          <div className="card absolute -bottom-7 left-4 flex max-w-[300px] items-center gap-3 p-4 pr-5 sm:left-8" style={{ boxShadow: "var(--shadow-2)" }} data-testid="hero-measure-card">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-maas-soft text-maas"><Ruler className="h-5 w-5" aria-hidden="true" /></span>
            <div>
              <p className="text-[15px] font-semibold text-ink">Gratis inmeten</p>
              <p className="text-[13px] text-slate-500">Bij u thuis, zonder verplichtingen</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const TrustStrip = () => (
  <section aria-label="Wat u van ons kunt verwachten" className="border-y border-slate-200 bg-slate-50" data-testid="trust-strip">
    <ul className="container-tight grid grid-cols-2 gap-x-4 gap-y-3 py-5 sm:grid-cols-3 lg:grid-cols-5">
      {TRUST_POINTS.map((t, i) => (
        <li key={t} className={`flex items-center gap-2 text-[14px] font-medium text-ink ${i === TRUST_POINTS.length - 1 ? "col-span-2 sm:col-span-1" : ""}`}>
          <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-slate-200"><Check className="h-3.5 w-3.5 text-maas" aria-hidden="true" /></span>
          {t}
        </li>
      ))}
    </ul>
  </section>
);
